import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Input, Row, Col, Checkbox, Typography, Tooltip, Button, Switch } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { accountActions } from '../../redux/actions';
import CreateChangeIn from '../CreateChangeIn/';
import ChooseSumType from '../ChooseSumType';
import Metrics from '../Metrics';
import Localization from '../../utils/localization';
import './styles.scss';
import ChooseBcCombine from '../ChooseBcCombine';

const { Title } = Typography;

const findAccountById = (nodes, accountId) => {
    for (const acc of nodes || []) {
        if (acc.account_id === accountId) return acc;

        const found = findAccountById(acc.children, accountId);
        if (found) return found;
    }

    return null;
};


const AccountManagement = ({
    accountStructure,
    account,
    isAccountManagementOpen,
    handleInsertAccount,
    handleCreateSubAccount,
    handleDeleteAccount,
    handleDeleteCustomTabAccount,
    handleClose,
}) => {
    const dispatch = useDispatch();
    const updatingAccount = useSelector((state) => state.accounts.updatingAccount);
    const reduxAccountStructure = useSelector((state) => state.accounts.accountStructure);
    const latestAccount = account; 

   
    const [accountNumber, setAccountNumber] = useState('');
    const [accountName, setAccountName] = useState('');
    const [financialGroupType, setFinancialGroupType] = useState(null);
    const [rollUp, setRollUp] = useState('');
    const [carry, setCarry] = useState(false);
    const [convergence, setConvergence] = useState(false);
    const [exclude, setExclude] = useState(false);
    const [autoAddChildren, setAutoAddChildren] = useState(false);
    const updatingCustomTabAccount = useSelector((state) => state.accounts.updatingCustomTabAccount);
   // const [excludeBC, setExcludeBC] = useState(false);
    const [isCreateChangeInOpen, setIsCreateChangeInOpen] = useState(false);
    const [isChooseSumTypeOpen, setIsChooseSumTypeOpen] = useState(false);
    const [isMetricsOpen, setIsMetricsOpen] = useState(false);
    const [isChooseBcCombineOpen, setIsChooseBcCombineOpen] = useState(false); 



    useEffect(() => {
        if (!account) return;

        const realAccount = account.account ?? account;

            setAccountNumber(realAccount.account_number);
            setAccountName(realAccount.name);
            setFinancialGroupType(account.financial_group_type);
            setRollUp(realAccount.roll_up ?? '');
            setCarry(realAccount.carry === true);
            setConvergence(realAccount.convergence === true);
            setExclude(realAccount.exclude === true);
            setAutoAddChildren(account.auto_add_children !== false);
        }, [account?.account_id, account?.custom_tab_accounts_id, account?.financial_group_type]);
        
        
        const handleOk = () => {
            if (account?.custom_tab_accounts_id != null) {
                handleCancel();
                return;
            }

            const modifiedAccount = {
                ...account,
                account_number: accountNumber,
                name: accountName,
                roll_up: rollUp,
                carry,
                convergence,
                exclude,
            };

            if (!updatingAccount) {
                dispatch(accountActions.updateAccount(account, modifiedAccount));
            }

            handleCancel();
        };

    const handleCancel = () => {
        handleClose();
    };

    const onChangeAccountNumber = (e) => {
        setAccountNumber(e.target.value);
    };

    const onChangeAccountName = (e) => {
        setAccountName(e.target.value);
    };

    const onChangeRollUpAdditive = (e) => {
        setRollUp('');
    };

    const onChangeRollUpSubtractive = (e) => {
        setRollUp('S');
    };

    const onChangeRollUpIgnore = (e) => {
        setRollUp('I');
    };

    const onChangeCarry = (e) => {
        setCarry(e.target.checked);
    };

    const onChangeConverge = (e) => {
        setConvergence(e.target.checked);
    };

    const onChangeExclude = (e) => {
        setExclude(e.target.checked);
    };
/*
    const onChangeExcludeAccountBC = (e) => {
        setExcludeBC(e.target.checked);
    };
    */

    const onChangeSetIsCreateChangeInOpen = () => {
        setIsCreateChangeInOpen(true);
    };

    const onChangeSetIsChooseSumTypeOpen = () => {
        setIsChooseSumTypeOpen(true);
    };

    const onChangeSetIsMetricsOpen = () => {
        setIsMetricsOpen(true);
    };

    const onChangeSetIsChooseBcCombineOpen = () => {
        setIsChooseBcCombineOpen(true);
    };

    const parseFinancialGroupToType = (fGroup) => {
        switch (fGroup) {
            case 1:
                return 'PL';
            case 2:
                return 'BSA';
            case 3:
                return 'BSL';
            case 4:
                return 'BSE';
            case 5:
                return 'CF';
            case 6:
                return 'DRVE';
            default:
                console.log('Could not parse financial group to financial type');
        }
    };

    const renderAccountManagementOptions = (account) => {
        const isCustomAccount = account?.custom_tab_accounts_id != null;

        const realAccount = account?.account ?? account;

        if (isCustomAccount) {
        return (
            <div>
                <Row>
                    <Col span={12}>
                        <Title level={5}>Account</Title>
                    </Col>
                    <Col span={12}>
                        <Input value={realAccount?.account_number} disabled />
                    </Col>
                </Row>

                <Row>
                    <Col span={12}>
                        <Title level={5}>Name</Title>
                    </Col>
                    <Col span={12}>
                        <Input value={realAccount?.name} disabled />
                    </Col>
                </Row>

                <Row style={{ margin: '12px 0' }} align="middle">
                    <Col span={12}>
                        <Title level={5} style={{ margin: 0 }}>
                            Add new sub-accounts automatically
                        </Title>
                    </Col>
                    <Col span={12}>
                        <Switch
                            checked={autoAddChildren}
                            loading={updatingCustomTabAccount}
                            onChange={(checked) => {
                                setAutoAddChildren(checked);
                                dispatch(
                                    accountActions.updateCustomTabAccountSettings(account, {
                                        autoAddChildren: checked,
                                    }),
                                );
                            }}
                        />
                    </Col>
                </Row>

                {account.hidden_children_count > 0 && (
                    <Typography.Paragraph type="secondary">
                        {account.hidden_children_count} of {account.real_children_count}{' '}
                        sub-accounts are not on this tab. They are still part of this
                        account's total and are shown as an "Other" line.
                    </Typography.Paragraph>
                )}

                <Button
                    danger
                    onClick={() => {
                        handleDeleteCustomTabAccount(account);
                        handleCancel();
                    }}
                >
                    Remove from custom tab
                </Button>
            </div>
        );
    }


        return (
            <div>
                <Row>
                    <Col span={12}>
                        <Title level={5} style={{ marginTop: '30px' }}>
                            {' '}
                            {Localization.accountStructure.accountNumber}{' '}
                        </Title>
                    </Col>
                    <Col span={12}>
                        <Input
                            value={accountNumber}
                            placeholder={Localization.accountStructure.accountNumberNotFound}
                            style={{ textAlign: 'right', marginTop: '30px' }}
                            onChange={onChangeAccountNumber}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col span={12}>
                        <Title level={5} style={{ marginTop: '15px' }}>
                            {' '}
                            {Localization.accountStructure.accountName}{' '}
                        </Title>
                    </Col>
                    <Col span={12}>
                        <Input
                            value={accountName}
                            placeholder={Localization.accountStructure.accountNameNotFound}
                            style={{ textAlign: 'right', marginTop: '15px' }}
                            onChange={onChangeAccountName}
                        />
                    </Col>
                </Row>
                <Row>
                    {!isCustomAccount ? (
                        <>
                            <Col span={8}>
                                <Title level={5} style={{ marginTop: '15px' }}>
                                    {' '}
                                    {Localization.accountStructure.accountRollUp}{' '}
                                </Title>
                            </Col>
                            <Col span={8}>
                                <Title level={5} style={{ marginTop: '15px' }}>
                                    {' '}
                                    {Localization.accountStructure.accountRollUpAdditive}{' '}
                                </Title>
                            </Col>
                            <Col span={8}>
                                <Tooltip title={Localization.accountStructure.accountRollUpAdditiveTooltip}>
                                    <Checkbox
                                        checked={rollUp === ''}
                                        style={{
                                            marginTop: '15px',
                                            float: 'right',
                                        }}
                                        onChange={onChangeRollUpAdditive}
                                    />
                                </Tooltip>
                            </Col>
                        </>
                    ) : null}
                </Row>
                {!isCustomAccount ? (
                    <>
                        <Row>
                            <Col span={8}></Col>
                            <Col span={8}>
                                <Title level={5} style={{ marginTop: '0' }}>
                                    {' '}
                                    {Localization.accountStructure.accountRollUpSubtractive}{' '}
                                </Title>
                            </Col>
                            <Col span={8}>
                                <Tooltip title={Localization.accountStructure.accountRollUpSubtractiveTooltip}>
                                    <Checkbox
                                        checked={rollUp === 'S'}
                                        style={{
                                            marginTop: '0',
                                            float: 'right',
                                        }}
                                        onChange={onChangeRollUpSubtractive}
                                    />
                                </Tooltip>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={8}></Col>
                            {account != null && !account.is_change_in && account.associated_account_id == null ? (
                                <>
                                    <Col span={8}>
                                        <Title level={5} style={{ marginTop: '0' }}>
                                            {' '}
                                            {Localization.accountStructure.accountRollUpIgnore}{' '}
                                        </Title>
                                    </Col>
                                    <Col span={8}>
                                        <Tooltip
                                            title={
                                                rollUp === 'I'
                                                    ? Localization.accountStructure.accountRollUpSubIgnoreTooltip
                                                    : Localization.accountStructure.accountRollUpIgnoreTooltip
                                            }
                                        >
                                            <Checkbox
                                                checked={rollUp === 'I'}
                                                style={{
                                                    marginTop: '0',
                                                    float: 'right',
                                                }}
                                                onChange={onChangeRollUpIgnore}
                                            />
                                        </Tooltip>
                                    </Col>
                                </>
                            ) : null}
                        </Row>
                    </>
                ) : null}
                {!isCustomAccount ? (
                    <>
                        <Row>
                            <Col span={8}>
                                <Title level={5} style={{ marginTop: '15px' }}>
                                    {' '}
                                    {Localization.accountStructure.accountExtra}{' '}
                                </Title>
                            </Col>
                            <Col span={8}>
                                <Title level={5} style={{ marginTop: '15px' }}>
                                    {' '}
                                    {Localization.accountStructure.accountExtraCarry}{' '}
                                </Title>
                            </Col>
                            <Col span={8}>
                                <Tooltip title={Localization.accountStructure.carryDependency}>
                                    <Checkbox
                                        checked={carry}
                                        style={{
                                            marginTop: '15px',
                                            float: 'right',
                                        }}
                                        onChange={onChangeCarry}
                                    />
                                </Tooltip>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={8}></Col>
                            <Col span={8}>
                                <Title level={5} style={{ marginTop: '0' }}>
                                    {' '}
                                    {Localization.accountStructure.accountExtraConverge}{' '}
                                </Title>
                            </Col>
                            <Col span={8}>
                                <Tooltip title={Localization.accountStructure.convergeDependancy}>
                                    <Checkbox
                                        checked={convergence}
                                        style={{
                                            marginTop: '0',
                                            float: 'right',
                                        }}
                                        onChange={onChangeConverge}
                                    />
                                </Tooltip>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={8}></Col>
                            <Col span={8}>
                                <Title level={5} style={{ marginTop: '0' }}>
                                    {' '}
                                    {Localization.accountStructure.accountExtraExclude}{' '}
                                </Title>
                            </Col>
                            <Col span={8}>
                                <Tooltip title={Localization.accountStructure.excludeDependancy}>
                                    <Checkbox
                                        checked={exclude}
                                        style={{
                                            marginTop: '0',
                                            float: 'right',
                                        }}
                                        onChange={onChangeExclude}
                                    />
                                </Tooltip>
                           </Col>
                        </Row>
                    </>
                ) : null}

                {/* Action buttons — replaces the old Row/Col layout */}
                <div
                    style={{
                        marginTop: '20px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                    }}
                >
                    {!isCustomAccount && (
                        <Button
                            style={{ width: '160px' }}
                            onClick={(e) => handleInsertAccount(e, account)}
                        >
                            Insert account
                        </Button>
                    )}

                    {!isCustomAccount && (
                        <Button
                            style={{ width: '160px' }}
                            onClick={() =>
                                handleCreateSubAccount(
                                    account,
                                    parseFinancialGroupToType(financialGroupType),
                                )
                            }
                        >
                            Add sub-account
                        </Button>
                    )}

                    {account != null && !isCustomAccount && (
                        <Button
                            style={{ width: '160px' }}
                            onClick={onChangeSetIsChooseSumTypeOpen}
                        >
                            Sum Type
                        </Button>
                    )}

                    {account != null &&
                        !isCustomAccount &&
                        account.associated_account_id == null && (
                            <Button
                                style={{ width: '160px' }}
                                onClick={onChangeSetIsMetricsOpen}
                            >
                                Metrics
                            </Button>
                        )}

                    {account != null &&
                        !isCustomAccount &&
                        !account.is_change_in &&
                        account.associated_account_id == null && (
                            <Button
                                style={{ width: '160px' }}
                                onClick={onChangeSetIsCreateChangeInOpen}
                            >
                                Create Change In
                            </Button>
                        )}

                    {account != null &&
                        !isCustomAccount &&
                        account.associated_account_id == null && (
                            <Button
                                style={{ width: '160px' }}
                                onClick={onChangeSetIsChooseBcCombineOpen}
                            >
                                BC Combine
                            </Button>
                        )}

                    {account != null && account.associated_account_id == null && (
                        <Tooltip title={Localization.accountStructure.accountDelete}>
                            <Button
                                danger
                                style={{ width: '160px', marginLeft: 'auto' }}
                                onClick={
                                    isCustomAccount
                                        ? () => {
                                              handleDeleteCustomTabAccount(account);
                                              handleCancel();
                                          }
                                        : () => {
                                              handleDeleteAccount(account);
                                              handleCancel();
                                          }
                                }
                            >
                                Delete account
                            </Button>
                        </Tooltip>
                    )}
                </div>

                {account != null && account.custom_tab_accounts_id == null ? (
                    <CreateChangeIn
                        accountStructure={accountStructure}
                        isCreateChangeInOpen={isCreateChangeInOpen}
                        setIsCreateChangeInOpen={setIsCreateChangeInOpen}
                        mainAccount={account}
                    />
                ) : null}
                {account != null && account.custom_tab_accounts_id == null ? (
                    <ChooseSumType
                        accountStructure={accountStructure}
                        isChooseSumTypeOpen={isChooseSumTypeOpen}
                        setIsChooseSumTypeOpen={setIsChooseSumTypeOpen}
                        mainAccount={account}
                    />
                ) : null}
                {account != null && account.custom_tab_accounts_id == null ? (
                    <Metrics
                        accountStructure={accountStructure}
                        isMetricsOpen={isMetricsOpen}
                        setIsMetricsOpen={setIsMetricsOpen}
                        mainAccount={account}
                    />
                ) : null}
                {account != null && account.custom_tab_accounts_id == null ? (
                    <ChooseBcCombine
                        accountStructure={accountStructure}
                        isChooseBcCombineOpen={isChooseBcCombineOpen}
                        setIsChooseBcCombineOpen={setIsChooseBcCombineOpen}
                        mainAccount={account}
                    />
                ) : null}
            </div>
        );
    };

    return (
        <Modal
            bodyStyle={{
                minHeight: '45vh',
                display: 'flex',
                flexDirection: 'column',
            }}
            title={Localization.accountStructure.accountManagement}
            key={`${account?.custom_tab_accounts_id ?? 'real'}-${account?.account_id ?? 'none'}`}
            visible={isAccountManagementOpen} // not deprecated in our version
            onOk={handleOk}
            confirmLoading={false}
            onCancel={handleCancel}
        >
            {renderAccountManagementOptions(account)}
            
        </Modal>
    );
};

export default AccountManagement;
